/**
 * React binding for the Assembly i18n framework. All pure logic lives in
 * `i18n-core.ts` (unit-testable from root vitest without React installed);
 * this module adds the Provider + hooks and re-exports the core so
 * callers only need to import from `./i18n`.
 *
 * See i18n-core.ts for locale resolution + translation semantics.
 * See memory: feedback_root_typecheck.md for why the split exists.
 */

import { createContext, useContext, useMemo, type ReactNode, createElement } from "react";
import {
  translate,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from "./i18n-core";

// Re-export the pure core so consumers only import from "./i18n".
export {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  isSupportedLocale,
  registerCatalogue,
  translate,
  resolveLocale,
  type SupportedLocale,
  type TranslationCatalogue,
  type LocaleInputs,
} from "./i18n-core";

// ── React context + hook ────────────────────────────────────────────────

type I18nContextValue = {
  locale: SupportedLocale;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  locale,
  children,
}: {
  locale: SupportedLocale;
  children: ReactNode;
}) {
  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      t: (key, params) => translate(locale, key, params),
    }),
    [locale],
  );
  return createElement(I18nContext.Provider, { value }, children);
}

/**
 * Hook - read the current locale + translation function. Callers OUTSIDE
 * an <I18nProvider> (e.g. tests, or an early-mounting component) get a
 * safe fallback that always returns en-GB translations. Never throws.
 */
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx) return ctx;
  return {
    locale: DEFAULT_LOCALE,
    t: (key, params) => translate(DEFAULT_LOCALE, key, params),
  };
}

/**
 * Convenience for callers who just want the current locale (e.g. to
 * pass to Intl.DateTimeFormat).
 */
export function useLocale(): SupportedLocale {
  return useT().locale;
}
